import React, { useEffect, useRef, useState } from "react";
import "./StrategicGanttView.scss";
import { DashChartProps } from "../../../DashCard";
import { useCentralCalls } from "@src/rest/CentralCalls";
import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";
import { ColorScheme } from "../../../ColorScheme";
type Task = {
    name: string;
    startDate: string;
    id: string;
    endDate: string;
    type: string;
    businessFunction?: string;
    children?: Task[];
};
type LevelStyle = {
    color: string;
    backgroundColor: string;
    progressBarColor:string;
};
const StrategicGanttView: React.FC<DashChartProps> = ({ item, projectVersionId, userId, projectId, filterParams }) => {
    const { executeStoredProcedure } = useCentralCalls();
    const tasksold: Task[] = [
        {
            name: "testOne",
            startDate: "2025-01-01",
            endDate: "2026-03-01",
            type: "Category",
            children: [
                {
                    name: "testTwo",
                    startDate: "2025-03-01",
                    endDate: "2025-04-01",
                    type: "Sub-Category",
                    businessFunction: "Finance",
                },
                {
                    name: "testThree",
                    startDate: "2025-04-01",
                    endDate: "2025-07-01",
                    type: "Sub-Category",
                    businessFunction: "HR",
                },
            ],
        },
        {
            name: "testFour",
            startDate: "2025-02-01",
            endDate: "2025-05-01",
            type: "Standalone",
        },
    ];
    const [ tasks, setTasks ]                                         = useState<[]>( [] ),
          ganttRef                                                    = useRef<HTMLDivElement>( null ),
          [ collapsedTasks, setCollapsedTasks ]                       = useState<Set<string>>(new Set()),
          [ selectedTypes, setSelectedTypes ]                         = useState<Set<string>>(new Set()),
          [ filteredTasks, setFilteredTasks ]                         = useState<Task[]>( [] ),
          [ selectedBusinessFunctions, setSelectedBusinessFunctions ] = useState<Set<string>>(new Set()),
          [ distinctTypes, setDistinctTypes ]                         = useState<string[]>( [] ),
          [ distinctBusinessFunctions, setDistinctBusinessFunctions ] = useState<string[]>( [] );

    const getDistinctValues = (tasks: Task[], key: "type" | "businessFunction"): string[] => {
        const values = new Set<string>();

        const collectValues = (taskList: Task[]) => {
            taskList.forEach((task) => {
                if (task.children) {
                    values.add(task[key]!)
                    collectValues(task.children); // Only look at children for `businessFunction`
                } else if (task[key]) {
                    values.add(task[key]!);
                }
            });
        };
        collectValues(tasks);
        return Array.from(values);
    };

    useEffect(() => {
        const fetchChartData = async () => {
            try {
                const data = await executeStoredProcedure('proc_GetStrategicGanttView', {
                    projectVersionId,
                    userId,
                    projectId,
                    filterParams,
                });
                console.log(data);
                setTasks(data);
    
                // Extract distinct values for `type` and `businessFunction`
                const types = new Set<string>();
                const businessFunctions = new Set<string>();
                const collectValues = (taskList: Task[]) => {
                    taskList.forEach((task) => {
                        if (task.type) types.add(task.type);
                        if (!task.children && task.businessFunction) {
                            businessFunctions.add(task.businessFunction);
                        }
                        if (task.children) {
                            collectValues(task.children);
                        }
                    });
                };
    
                collectValues(data);
                //console.log(data);
                const distinctTypesArray = Array.from(types);
                const distinctBusinessFunctionsArray = Array.from(businessFunctions);
    
                setDistinctTypes(distinctTypesArray);
                setSelectedTypes(new Set(distinctTypesArray));
    
                setDistinctBusinessFunctions(distinctBusinessFunctionsArray);
                setSelectedBusinessFunctions(new Set(distinctBusinessFunctionsArray));
                console.log(new Set(distinctBusinessFunctionsArray));
            } catch (error) {
                console.error('Error fetching Gantt data:', error);
            }
        };
    
        fetchChartData();
    }, [projectVersionId, item, filterParams]);
    useEffect(() => {
        // Filters tasks by `type`
        const filterTasksByType = (tasks: Task[]): Task[] => {
            return tasks
                .map((task) => {
                    if (task.children) {
                        const filteredChildren = filterTasksByType(task.children);
                        const matchesType = selectedTypes.size === 0 || selectedTypes.has(task.type);
    
                        // Include parent tasks if they have children or match the type
                        return filteredChildren.length > 0 || matchesType
                            ? { ...task, children: filteredChildren }
                            : null;
                    }
    
                    const matchesType = selectedTypes.size === 0 || selectedTypes.has(task.type);
                    return matchesType ? task : null;
                })
                .filter((task): task is Task => task !== null);
        };
    
        // Filters leaf tasks by `businessFunction`
        const filterTasksByBusinessFunction = (tasks: Task[]): Task[] => {
            return tasks
                .map((task) => {
                    if (task.children) {
                        const filteredChildren = filterTasksByBusinessFunction(task.children);
                        return { ...task, children: filteredChildren };
                    }
    
                    const matchesBusinessFunction =
                        selectedBusinessFunctions.size === 0 ||
                        (task.businessFunction && selectedBusinessFunctions.has(task.businessFunction));
                    return matchesBusinessFunction ? task : null;
                })
                .filter((task): task is Task => task !== null);
        };
    
        // Apply both filters sequentially
        const typeFilteredTasks = filterTasksByType(tasks);
        const fullyFilteredTasks = filterTasksByBusinessFunction(typeFilteredTasks);
        setFilteredTasks(fullyFilteredTasks);
    }, [tasks, selectedTypes, selectedBusinessFunctions]);
    // useEffect(() => {
    //     // Update filtered tasks whenever `tasks` or `selectedTypes` change
    //     const filterTasks = (tasks: Task[]): Task[] => {
    //         return tasks
    //             .map((task) => {
    //                 if (task.children) {
    //                     const filteredChildren = filterTasks(task.children);
    //                     const matchesType = selectedTypes.has(task.type);

    //                     // Include the task if it matches the type or has visible children
    //                     return filteredChildren.length > 0 || matchesType
    //                         ? { ...task, children: filteredChildren }
    //                         : null;
    //                 }

    //                 // Match leaf tasks based only on type
    //                 const matchesType = selectedTypes.has(task.type);

    //                 return matchesType ? task : null;
    //             })
    //             .filter((task): task is Task => task !== null);
    //     };

    //     setFilteredTasks(filterTasks(tasks));
    // }, [tasks, selectedTypes]);
    const exportToPowerPoint = async () => {
        if (!ganttRef.current) return;

        // Capture the component as an image
        const canvas = await html2canvas(ganttRef.current);
        const imgData = canvas.toDataURL("image/png");

        // Create PowerPoint presentation
        const pptx = new PptxGenJS();
        const slide = pptx.addSlide();

        slide.addImage({
            data: imgData,
            x: 0.5, // Margin from the left
            y: 0.5, // Margin from the top
            w: 9, // Width in inches
            h: 5, // Height in inches
        });

        // Save the presentation
        pptx.writeFile({ fileName: "StrategicGanttView.pptx" });
    };
    const toggleCollapse = (taskName: string) => {
        setCollapsedTasks((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(taskName)) {
                newSet.delete(taskName);
            } else {
                newSet.add(taskName);
            }
            return newSet;
        });
    };

    const filterTasks = (tasks: Task[]): Task[] => {
        return tasks
            .map((task) => {
                if (task.children) {
                    const filteredChildren = filterTasks(task.children);
                    const matchesType = selectedTypes.size === 0 || selectedTypes.has(task.type);

                    // Include the task if it matches the type or has visible children
                    return filteredChildren.length > 0 || matchesType
                        ? { ...task, children: filteredChildren }
                        : null;
                }

                // Match leaf tasks based only on type
                const matchesType = selectedTypes.size === 0 || selectedTypes.has(task.type);

                return matchesType ? task : null;
            })
            .filter((task): task is Task => task !== null);
    };
    // const filterTasks = (tasks: Task[]): Task[] => {
    //     return tasks
    //         .map((task) => {
    //             if (task.children) {
    //                 const filteredChildren = filterTasks(task.children);
    //                 const matchesType = selectedTypes.size === 0 || selectedTypes.has(task.type);
    //                 const matchesBusinessFunction =
    //                     selectedBusinessFunctions.size === 0 ||
    //                     (task.businessFunction && selectedBusinessFunctions.has(task.businessFunction));

    //                 return filteredChildren.length > 0 || (matchesType && matchesBusinessFunction)
    //                     ? { ...task, children: filteredChildren }
    //                     : null;
    //             }

    //             const matchesType = selectedTypes.size === 0 || selectedTypes.has(task.type);
    //             const matchesBusinessFunction =
    //                 selectedBusinessFunctions.size === 0 || selectedBusinessFunctions.has(task.businessFunction || "");

    //             return matchesType && matchesBusinessFunction ? task : null;
    //         })
    //         .filter((task): task is Task => task !== null);
    // };
    //const filteredTasks = filterTasks(tasks);

    const getMinMaxYears = (tasks: Task[]): { minYear: number; maxYear: number } => {
        let years: number[] = [];
        const collectYears = (taskList: Task[]) => {
            taskList.forEach((task) => {
                years.push(new Date(task.startDate).getFullYear());
                years.push(new Date(task.endDate).getFullYear());
                if (task.children) collectYears(task.children);
            });
        };
        collectYears(tasks);
        return { minYear: Math.min(...years), maxYear: Math.max(...years) };
    };

    const { minYear, maxYear } = getMinMaxYears(filteredTasks);

    const calculateProgressDates = (task: Task): { startDate: string; endDate: string } => {
        
        if (!task.children || task.children.length === 0 ) {
            return { startDate: task.startDate, endDate: task.endDate };
        }

        const allDates = task.children
            .map((child) => {
                const dates = calculateProgressDates(child);
                return [new Date(dates.startDate), new Date(dates.endDate)];
            })
            .reduce((acc, curr) => acc.concat(curr), []);

        const minDate = new Date(Math.min(...allDates.map((date) => date.getTime())));
        const maxDate = new Date(Math.max(...allDates.map((date) => date.getTime())));

        return {
            startDate: minDate.toISOString().split("T")[0],
            endDate: maxDate.toISOString().split("T")[0],
        };
    };

    const renderTask = (task: Task, level: number) => {
        
        const progressDates = calculateProgressDates(task);

        const taskStart = new Date(progressDates.startDate);
        const taskEnd = new Date(progressDates.endDate);

        const totalYears = maxYear - minYear + 1;
        const startPercent =
            ((taskStart.getFullYear() - minYear) * 12 + taskStart.getMonth()) /
            (totalYears * 12);
        const endPercent =
            ((taskEnd.getFullYear() - minYear) * 12 + taskEnd.getMonth()) /
            (totalYears * 12);

        const isCollapsed = collapsedTasks.has(task.name);

        return (
            <div key={task.id} >
                <div className="task-row" >
                    <div
                        className="task-name"
                        style={{ paddingLeft: `${level * 25}px`,backgroundColor: ColorScheme.lightGrey }}
                        onClick={() => task.children && toggleCollapse(task.name)}
                    >
                        {task.children && (
                            <span className="collapse-icon">{isCollapsed ? "+" : "-"}</span>
                        )}
                        {task.name}
                    </div>
                    <div className="task-bar-container" >
                        <div
                            className="progress-bar"
                            style={{
                                left: `${startPercent * 100}%`,
                                width: `${(endPercent - startPercent) * 100}%`,
                                backgroundColor:ColorScheme.darkPurple
                            }}
                        ></div>
                    </div>
                </div>
                {task.children && !isCollapsed && (
                    <div className="task-children">
                        {task.children.map((child) => renderTask(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    const handleCheckboxChange = (key: "type" | "businessFunction", value: string) => {
        const setState = key === "type" ? setSelectedTypes : setSelectedBusinessFunctions;
        setState((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return newSet;
        });
    };

    return (
        <div style={{ width: '1000%' }}>
            <button onClick={exportToPowerPoint} className="export-button">
                Export to PowerPoint
            </button>
            <div ref={ganttRef} className="strategic-gantt-view">
                {/* Filters */}
                <div className="filters">
                    <div className="filter">
                        <strong>Filter by Type</strong>
                        {distinctTypes.map((type) => (
                            <label key={type}>
                                <input
                                    type="checkbox"
                                    checked={selectedTypes.has(type)}
                                    onChange={() => handleCheckboxChange("type",type)}
                                />
                                {type}
                            </label>
                        ))}
                    </div>
                    <div className="filter">
                        <strong>Filter by Business Function</strong>
                        {distinctBusinessFunctions.map((businessFunction) => (
                            <label key={businessFunction}>
                                <input
                                    type="checkbox"
                                    checked={selectedBusinessFunctions.has(businessFunction)}
                                    onChange={() => handleCheckboxChange("businessFunction", businessFunction)}
                                />
                                {businessFunction}
                            </label>
                        ))}
                    </div>
                </div>
                {/* Header Section */}
                <div className="header" style={{backgroundColor: ColorScheme.darkPurple, color:ColorScheme.white}}>
                    <div className="task-name-column">Tasks</div>
                    <div className="timeline-header">
                        {Array.from({ length: 5 }, (_, i) => (
                            <div key={i} className="timeline-year">
                                {2025 + i}
                            </div>
                        ))}
                    </div>
                </div>
                {/* Task Rows */}
                <div className="task-container">
                    <div className="task-grid">
                        {filteredTasks.map((task) => renderTask(task, 0))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StrategicGanttView;